'use client';

import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { ChatApi } from '@ps/api/chat';
import type { ChatMessageData } from '@ps/api/types';
import { auth } from '@ps/functions/firebase/auth';
import {
  useBehaviorSubject,
  useBehaviorSubjectUpdater,
} from '@ps/functions/hooks/useBehaviorSubject';
import { withClientAPI } from '@ps/functions/utils/api';
import { type Draft, produce } from 'immer';
import { BehaviorSubject, debounceTime, skip } from 'rxjs';

import { mapSet } from '../../functions/mapSet';
import { privateMessageEventDataToChatMessage } from '../../functions/privateMessageEventDataToChatMessage';
import { useChatGatewayEvent } from '../../hooks/useChatGateway';
import { useActiveChat, useChatStorage } from '../../hooks/useGlobalChat';
import type { ChatSession } from '../../types/ChatSession';
import { ChatSessionType } from '../../types/ChatSessionType';
import { SocketEvent } from '../../types/SocketEvent';
import {
  ChatSessionContext,
  type ChatSessionContextValue,
  type SessionListStatus,
} from './context';

const kLimit = 500;

interface Props {
  children?: ReactNode;
}

/**
 * Sessions list state for the chat module. Backed by three
 * BehaviorSubjects (sessionType$, sessions$, status$) so consumers
 * can selectively subscribe. Saves to `ChatStorageManager` on a
 * 1-second debounce and folds incoming `private.message` gateway
 * events into the cached list (or refreshes if the session is
 * unknown).
 */
export function ChatSessionProvider({ children }: Props) {
  const storage = useChatStorage();
  const isLoadRequested = useRef(false);
  const activeChat = useActiveChat();
  const activeTargetUserId = activeChat?.targetUserId;

  const [sessionType$] = useState(
    () => new BehaviorSubject<ChatSessionType>(ChatSessionType.Private),
  );

  const [sessions$] = useState(
    () => new BehaviorSubject(new Map<ChatSessionType, ChatSession[]>()),
  );

  const [status$] = useState(
    () =>
      new BehaviorSubject<SessionListStatus>({
        error: undefined,
        isLoading: false,
        isRefreshing: false,
        isCached: false,
      }),
  );

  const updateStatus = useBehaviorSubjectUpdater(status$);
  const [sessionType] = useBehaviorSubject(sessionType$);

  useEffect(() => {
    isLoadRequested.current = false;
    void storage.getSessions(sessionType).then(sessions => {
      updateStatus(x => ({ ...x, isCached: sessions != null }));
      sessions$.next(
        mapSet(
          sessions$.value,
          sessionType,
          sessions ?? sessions$.value.get(sessionType) ?? [],
        ),
      );
    });
  }, [sessionType, sessions$, storage, updateStatus]);

  useEffect(() => {
    const sub = sessions$
      .pipe(skip(1), debounceTime(1000))
      .subscribe(sessions => {
        if (!status$.getValue().isCached) {
          updateStatus(x => ({ ...x, isCached: true }));
        }
        for (const [type, list] of sessions.entries()) {
          void storage.saveSessions(type, list);
        }
      });
    return () => sub.unsubscribe();
  }, [sessions$, storage, status$, updateStatus]);

  const query = useCallback(async (): Promise<ChatSession[]> => {
    const api = withClientAPI(ChatApi);
    const res = await api.chatControllerGetSessions({
      sessionType,
      cursor: 0,
      limit: kLimit,
    });
    return res.data.map<ChatSession>(x => ({
      id: x.id,
      sessionType: (x.type as ChatSessionType) ?? ChatSessionType.Private,
      sessionId: x.sessionId,
      type: x.type,
      content: x.content,
      avatar: x.avatar,
      targetUserId: x.targetUserId,
      targetUserName: x.targetUserName,
      targetAvatar: x.targetAvatar,
      lastMessageTime: x.lastMessageTime
        ? new Date(x.lastMessageTime)
        : undefined,
      unreadCount: x.unreadCount,
      lastMessage: x.lastMessage
        ? privateMessageEventDataToChatMessage(
            x.lastMessage as unknown as ChatMessageData,
          )
        : undefined,
    }));
  }, [sessionType]);

  const loadSessions = useCallback(
    async (
      type: ChatSessionType,
      options?: { refresh?: boolean },
    ): Promise<void> => {
      if (auth().currentUser == null) return;
      isLoadRequested.current = true;

      const statusToUpdate: Partial<SessionListStatus> = { error: undefined };
      if (options?.refresh) {
        statusToUpdate.isRefreshing = true;
      } else {
        statusToUpdate.isLoading = true;
      }
      updateStatus(x => ({ ...x, ...statusToUpdate }));

      try {
        const sessions = await query();
        sessions$.next(mapSet(sessions$.value, type, sessions));
      } catch (e) {
        statusToUpdate.error = e as Error;
        console.error('An error occurred while loading chat sessions:', e);
      } finally {
        if (options?.refresh) {
          statusToUpdate.isRefreshing = false;
        } else {
          statusToUpdate.isLoading = false;
        }
        updateStatus(x => ({ ...x, ...statusToUpdate }));
      }
    },
    [query, sessions$, updateStatus],
  );

  const updateSessions = useCallback(
    (
      type: ChatSessionType,
      recipe: (draft: Draft<ChatSession[]>) => void,
    ): void => {
      const sessions = sessions$.getValue().get(type) ?? [];
      const updated = produce(sessions, recipe);
      if (updated !== sessions) {
        sessions$.next(mapSet(sessions$.value, type, updated));
      }
    },
    [sessions$],
  );

  useChatGatewayEvent(
    SocketEvent.NewMsg,
    data => {
      const msg = privateMessageEventDataToChatMessage(data);
      const msgSessionType = msg.sessionType ?? ChatSessionType.Private;
      const sessions = sessions$.getValue().get(msgSessionType) ?? [];
      const index = sessions.findIndex(
        item => item.sessionId === msg.sessionId,
      );
      if (index < 0) {
        if (isLoadRequested.current) {
          void loadSessions(msgSessionType);
        }
        return;
      }
      updateSessions(msgSessionType, draft => {
        const item = draft[index];
        item.lastMessageTime = new Date(msg.time);
        item.lastMessage = msg;
        if (activeTargetUserId === item.targetUserId) {
          if (item.unreadCount > 0) item.unreadCount = 0;
        } else {
          item.unreadCount++;
          item.lastMessage = msg;
        }
        draft.splice(index, 1);
        draft.unshift(item);
      });
    },
    'chat-session-context',
  );

  useEffect(() => {
    if (!activeTargetUserId) return;
    updateSessions(sessionType, draft => {
      const index = draft.findIndex(x => x.targetUserId === activeTargetUserId);
      if (index < 0) return;
      const session = draft[index];
      if (session.unreadCount > 0) {
        // Zero the row badge locally on open; the shared inbox counts
        // (sidebar + tabs) reconcile via the InboxUnread bridge's refresh.
        session.unreadCount = 0;
      }
    });
  }, [activeTargetUserId, sessionType, updateSessions]);

  const value = useMemo<ChatSessionContextValue>(
    () => ({
      sessionType$,
      sessions$,
      status$,
      loadSessions,
      updateSessions,
    }),
    [sessionType$, sessions$, status$, loadSessions, updateSessions],
  );

  return (
    <ChatSessionContext.Provider value={value}>
      {children}
    </ChatSessionContext.Provider>
  );
}
